import { performPostRequest } from '@atlassian/jira-fetch/src/index.tsx';
import {
	createResource,
	useResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
} from '@atlassian/react-resource-router';
import { getIssueKey } from '@atlassian/jira-router-resources-utils/src/utils/get-issue-key/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { QUERY } from './gql.tsx';
import type {
	IssueViewDefaultCommentBehaviour,
	IssueViewDefaultCommentBehaviourResponse,
} from './types.tsx';

const isDefaultCommentBehaviourEnabled = async (
	cloudId: string,
): Promise<IssueViewDefaultCommentBehaviour> => {
	if (!fg('jsm_comment_default_behavior_issue_view')) {
		return 'INTERNAL_NOTE';
	}

	const defaultCommentBehaviourResponse =
		await performPostRequest<IssueViewDefaultCommentBehaviourResponse>('/gateway/api/graphql', {
			body: JSON.stringify({
				query: QUERY,
				variables: {
					cloudId,
				},
			}),
			headers: {
				'content-type': 'application/json',
			},
		});

	const defaultCommentBehaviour =
		defaultCommentBehaviourResponse.data?.jira_jiraServiceManagementDefaultCommentBehavior
			?.issueView;

	switch (defaultCommentBehaviour) {
		case 'INTERNAL_NOTE':
			return 'INTERNAL_NOTE';
		case 'REPLY_TO_CUSTOMER':
			return 'REPLY_TO_CUSTOMER';
		default:
			fireErrorAnalytics({
				error: new Error(
					`Unexpected value for JSM Issue View Default Comment Behaviour: ${defaultCommentBehaviour}`,
				),
				sendToPrivacyUnsafeSplunk: true,
				meta: {
					id: 'jsmIssueViewDefaultCommentBehaviourResource',
					packageName: 'jiraRouterResourcesJsmIssueView',
					teamName: 'jsd-shield',
				},
			});
			return 'INTERNAL_NOTE';
	}
};

const jsmIssueViewDefaultCommentBehaviourResource = createResource({
	type: 'JSM_ISSUE_VIEW_DEFAULT_COMMENT_BEHAVIOUR_RESOURCE',
	getKey: ({ match }: RouterContext) => {
		const issueKey = getIssueKey(match);

		return issueKey || '';
	},
	getData: async ({ match }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
		const cloudId = tenantContext.cloudId;
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			return 'null';
		}

		try {
			return await isDefaultCommentBehaviourEnabled(cloudId);
		} catch (error: unknown) {
			fireErrorAnalytics({
				error: error instanceof Error ? error : new Error(String(error)),
				sendToPrivacyUnsafeSplunk: true,
				meta: {
					id: 'jsmIssueViewDefaultCommentBehaviourResource',
					packageName: 'jiraRouterResourcesJsmIssueView',
					teamName: 'jsd-shield',
				},
			});

			return 'INTERNAL_NOTE';
		}
	},
});

export const getJsmIssueViewDefaultCommentBehaviourResource = () =>
	jsmIssueViewDefaultCommentBehaviourResource;

export const useJsmIssueViewDefaultCommentBehaviourResource = () => {
	return useResource(jsmIssueViewDefaultCommentBehaviourResource);
};
